import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Tabs,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Table,
  Modal,
  Popconfirm,
  message,
  Divider,
  Row,
  Col,
  DatePicker,
} from 'antd';
import { EditOutlined, SaveOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined, PlusCircleOutlined, MinusCircleOutlined, ThunderboltOutlined, EnvironmentOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useStore } from '../../stores/root-store-context';
import { Contact, ExternalCompany, CrmEvent } from '../../api/CRMApiClient';
import { ContactValidationKind } from '../../enums/ContactValidationKind';
import ERPLayout from '../../components/layout/ERPLayout';
import DuplicateWarning from '../../components/crm/DuplicateWarning';
import CrmAuditInfo from '../../components/crm/CrmAuditInfo';
import CrmHistoryTab from '../../components/crm/CrmHistoryTab';
import CrmAttachmentsTab from '../../components/crm/CrmAttachmentsTab';
import FormCheckboxDropdown from '../../components/crm/FormCheckboxDropdown';
import {
  findSimilarCompanies,
  findDuplicateEdrpou,
  findSimilarContacts,
  findDuplicateContactEmail,
  findDuplicateContactPhone,
  checkAdditionalInfoDuplicate,
  isValidPhoneNumber,
} from '../../helpers/crmDeduplication';

dayjs.extend(utc);
dayjs.extend(timezone);

const toLocal = (iso: string) => dayjs(iso).local();
const toApi = (d: Dayjs) => d.utc().format('YYYY-MM-DDTHH:mm:ss');

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const ExternalCompanyDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  const isCreateMode = !companyId || companyId === 'new';
  const companyIdNumber = useMemo(
    () => (!isCreateMode && !isNaN(Number(companyId)) ? Number(companyId) : null),
    [isCreateMode, companyId],
  );

  const [form] = Form.useForm<ExternalCompany>();
  const [searchParams] = useSearchParams();
  // AI address parsing: pasted text and loading flag per address row (keyed by Form.List field name)
  const [addressPaste, setAddressPaste] = useState<Record<number, string>>({});
  const [addressParsing, setAddressParsing] = useState<Record<number, boolean>>({});
  const parseAddressInto = async (fieldName: number) => {
    const text = (addressPaste[fieldName] || '').trim();
    if (!text) return;
    setAddressParsing(prev => ({ ...prev, [fieldName]: true }));
    try {
      const r = await crmStore.parseAddress(text);
      const current = form.getFieldValue(['addresses', fieldName]) || {};
      form.setFieldValue(['addresses', fieldName], {
        ...current,
        country: r.country || current.country,
        state: r.state || current.state,
        city: r.city || current.city,
        addressLine1: r.addressLine1 || current.addressLine1,
        zipCode: r.zipCode || current.zipCode,
        plusCode: r.plusCode || current.plusCode,
        locationName: current.locationName || r.locationName,
        latitude: r.latitude ?? current.latitude,
        longitude: r.longitude ?? current.longitude,
      });
      setAddressPaste(prev => ({ ...prev, [fieldName]: '' }));
      message.success(r.coordinatesAccuracy === 'approximate' ? t('AddressParsedApprox') : t('AddressParsed'));
    } catch (e: any) {
      const code = e?.message;
      message.error(code === 'AiNotConfigured' || code === 'AiUnavailable' ? t('AddressAiUnavailable') : t('AddressParseFailed'));
    } finally {
      setAddressParsing(prev => ({ ...prev, [fieldName]: false }));
    }
  };
  const mapUrl = (a: any): string | null => {
    if (!a) return null;
    if (a.latitude != null && a.longitude != null) return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
    const q = [a.addressLine1, a.city, a.state, a.zipCode, a.country].filter((x: any) => x && String(x).trim()).join(', ');
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  };
  const [editing, setEditing] = useState<boolean>(isCreateMode || searchParams.get('edit') === '1');
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [companyRecord, setCompanyRecord] = useState<ExternalCompany | null>(null);

  const [contactModalOpen, setContactModalOpen] = useState<boolean>(false);
  const [contactForm] = Form.useForm();
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const [eventModalVisible, setEventModalVisible] = useState<boolean>(false);
  const [eventForm] = Form.useForm();
  const [editingEvent, setEditingEvent] = useState<CrmEvent | null>(null);

  const [relatedContacts, setRelatedContacts] = useState<Contact[]>([]);
  const [relatedEvents, setRelatedEvents] = useState<any[]>([]);

  // ─────────── Real-time duplicate & similarity checks ───────────
  const watchedCompanyName = Form.useWatch('name', form);
  const watchedCompanyEdrpou = Form.useWatch('edrpou', form);

  const tenant = applicationStore.company?.Tenant;
  const tenantPath = tenant ? `/${tenant}` : '';

  const companyNameDuplicates = useMemo(() => {
    if (!watchedCompanyName) return null;
    return findSimilarCompanies(watchedCompanyName, crmStore.externalCompanies, companyIdNumber);
  }, [watchedCompanyName, crmStore.externalCompanies, companyIdNumber]);

  const companyEdrpouDuplicate = useMemo(() => {
    if (!watchedCompanyEdrpou) return null;
    return findDuplicateEdrpou(watchedCompanyEdrpou, crmStore.externalCompanies, companyIdNumber);
  }, [watchedCompanyEdrpou, crmStore.externalCompanies, companyIdNumber]);

  // Contact modal watches
  const watchedContactName = Form.useWatch('name', contactForm);
  const watchedContactSurname = Form.useWatch('surname', contactForm);
  const watchedContactEmail = Form.useWatch('email', contactForm);
  const watchedContactPhone = Form.useWatch('phoneNumber', contactForm);
  const watchedContactAdditionalInfos = Form.useWatch('additionalInfos', contactForm);

  const contactNameDuplicates = useMemo(() => {
    if (!watchedContactName && !watchedContactSurname) return null;
    return findSimilarContacts(
      watchedContactName || '',
      watchedContactSurname || '',
      crmStore.contacts,
      editingContact?.id,
    );
  }, [watchedContactName, watchedContactSurname, crmStore.contacts, editingContact?.id]);

  const contactEmailDuplicate = useMemo(() => {
    if (!watchedContactEmail) return null;
    return findDuplicateContactEmail(watchedContactEmail, crmStore.contacts, editingContact?.id);
  }, [watchedContactEmail, crmStore.contacts, editingContact?.id]);

  const contactPhoneDuplicate = useMemo(() => {
    if (!watchedContactPhone) return null;
    return findDuplicateContactPhone(watchedContactPhone, crmStore.contacts, editingContact?.id);
  }, [watchedContactPhone, crmStore.contacts, editingContact?.id]);

  const buildCompositeName = useCallback(
    (companyIds?: number[], contactIds?: number[], range?: [Dayjs, Dayjs]) => {
      const parts: string[] = [];
      if (companyIds?.length) {
        const first = crmStore.externalCompanies.find(c => c.id === companyIds[0]);
        if (first) parts.push(first.name);
        if (companyIds.length > 1) parts.push(`+${companyIds.length - 1}`);
      }
      if (contactIds?.length) {
        const first = crmStore.contacts.find(c => c.id === contactIds[0]);
        if (first) parts.push(`${first.name} ${first.surname}`.trim());
        if (contactIds.length > 1) parts.push(`+${contactIds.length - 1}`);
      }
      if (range?.[0]) parts.push(range[0].format('DD/MM/YYYY'));
      return parts.filter(Boolean).join(' — ');
    },
    [crmStore.externalCompanies, crmStore.contacts],
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          crmStore.getClientRoles(),
          crmStore.getExternalCompanies(),
          crmStore.getContacts(),
          crmStore.getCrmEventTypes(),
          crmStore.getCategories(),
          crmStore.getContactTypes(),
        ]);

        if (!isCreateMode && companyIdNumber !== null) {
          const company = await crmStore.ApiStore.CRMApiClient.getExternalCompanyById(
            companyIdNumber,
          );
          if (company) {
            setCompanyRecord(company);
            form.setFieldsValue(company);
            const contacts = await crmStore.getContactsByExternalCompany(companyIdNumber);
            setRelatedContacts(contacts);
            const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByExternalCompany(
              companyIdNumber,
            );
            setRelatedEvents(events);
          }
        } else {
          form.setFieldsValue({ name: '', edrpou: '', description: '', addresses: [] });
        }
      } catch (err) {
        console.error('Error loading company data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [crmStore, isCreateMode, companyIdNumber, form]);

  const contactTypeOptions = useMemo(() => {
    const dbTypes = crmStore.contactTypes.map(ct => ct.name).filter(Boolean);
    const defaultTypes = ['Email', 'Phone', 'Telegram', 'Skype', 'LinkedIn'];
    const baseList = dbTypes.length > 0 ? dbTypes : defaultTypes;
    const result: string[] = [];
    const seen = new Set<string>();
    for (const name of baseList) {
      const lower = name.toLowerCase().trim();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(name);
      }
    }
    return result;
  }, [crmStore.contactTypes]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      // Address rows: the location label is required by the server, derive it when left empty
      values.addresses = (values.addresses || []).map((a: any) => ({
        ...a,
        locationName: (a.locationName || '').trim() || [a.city, a.country].filter(Boolean).join(', ') || t('Address'),
      }));
      if (isCreateMode) {
        const created = await crmStore.addExternalCompany(values);
        if (created?.id) {
          message.success(t('CreatedSuccessfully'));
          navigate(`/${applicationStore.company?.Tenant}/crm/company/${created.id}`);
        } else {
          message.success(t('CreatedSuccessfully'));
          navigate(`/${applicationStore.company?.Tenant}/crm/companies`);
        }
      } else if (companyIdNumber !== null) {
        await crmStore.updateExternalCompany(companyIdNumber, values);
        setEditing(false);
        const refreshed = await crmStore.ApiStore.CRMApiClient.getExternalCompanyById(companyIdNumber);
        if (refreshed) setCompanyRecord(refreshed);
        message.success(t('SavedSuccessfully'));
      }
    } catch {}
  };

  const handleCancel = () => {
    form.resetFields();
    const company = crmStore.externalCompanies.find(c => c.id === companyIdNumber);
    if (company) {
      form.setFieldsValue({
        ...company,
        addresses: company.addresses || [],
      });
    }
    setEditing(false);
  };

  const onDelete = async () => {
    if (companyIdNumber === null) return;
    await crmStore.deleteExternalCompany(companyIdNumber);
    navigate(`/${applicationStore.company?.Tenant}/crm/companies`);
  };

  const refreshContacts = async () => {
    if (companyIdNumber === null) return;
    const contacts = await crmStore.getContactsByExternalCompany(companyIdNumber);
    setRelatedContacts(contacts);
  };

  const openAddContact = () => {
    if (companyIdNumber === null) return;
    setEditingContact(null);
    contactForm.resetFields();
    contactForm.setFieldsValue({
      externalCompanyId: companyIdNumber,
      additionalInfos: [],
      name: '',
      surname: '',
      email: '',
      phoneNumber: '',
      roleId: null,
    });
    setContactModalOpen(true);
  };

  const openEditContact = async (c: Contact) => {
    setEditingContact(c);
    const detailed = await crmStore.getContactById(c.id);
    const target = detailed || c;
    contactForm.setFieldsValue({
      ...target,
      additionalInfos: target.additionalInfos || [],
    });
    setContactModalOpen(true);
  };

  const hasExactContactDuplicate = Boolean(
    contactPhoneDuplicate ||
    contactEmailDuplicate ||
    contactNameDuplicates?.exactMatch
  );

  const saveContact = async () => {
    try {
      const vals = await contactForm.validateFields();

      if (contactPhoneDuplicate) {
        message.error(
          `${t('ContactWithPhoneExists', 'Контакт с таким номером телефона уже существует')}: ${contactPhoneDuplicate.name} ${contactPhoneDuplicate.surname}`
        );
        return;
      }

      if (contactEmailDuplicate) {
        message.error(
          `${t('ContactWithEmailExists', 'Контакт с таким email уже существует')}: ${contactEmailDuplicate.name} ${contactEmailDuplicate.surname}`
        );
        return;
      }

      if (contactNameDuplicates?.exactMatch) {
        message.error(
          `${t('ContactWithNameExists', 'Контакт с таким именем и фамилией уже существует')}: ${contactNameDuplicates.exactMatch.name} ${contactNameDuplicates.exactMatch.surname}`
        );
        return;
      }

      if (editingContact) {
        const ok = await crmStore.updateContact(editingContact.id, vals);
        if (ok === false) {
          message.error(crmStore.error || t('SomethingWentWrong'));
          return;
        }
      } else {
        const created = await crmStore.addContact(vals);
        if (!created) {
          message.error(crmStore.error || t('SomethingWentWrong'));
          return;
        }
      }
      setContactModalOpen(false);
      await refreshContacts();
      message.success(editingContact ? t('SavedSuccessfully') : t('CreatedSuccessfully'));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteContact = async (id: number) => {
    await crmStore.deleteContact(id);
    await refreshContacts();
    message.success(t('DeletedSuccessfully'));
  };

  const refreshEvents = async () => {
    if (companyIdNumber === null) return;
    const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByExternalCompany(
      companyIdNumber,
    );
    setRelatedEvents(events);
  };

  const openAddEvent = () => {
    if (companyIdNumber === null) return;
    setEditingEvent(null);
    const now = dayjs();
    eventForm.setFieldsValue({
      dateRange: [now, now.clone().add(30, 'minutes')],
      externalCompanyIds: [companyIdNumber],
      contactIds: [],
      name: '',
      description: '',
      typeId: null,
    });
    setEventModalVisible(true);
  };

  const openEditEvent = (event: CrmEvent) => {
    setEditingEvent(event);
    const companyIds =
      event.externalCompanies && event.externalCompanies.length > 0
        ? event.externalCompanies.map(c => c.id)
        : companyIdNumber !== null
        ? [companyIdNumber]
        : [];

    eventForm.setFieldsValue({
      name: event.name,
      description: event.description,
      typeId: event.typeId,
      dateRange: [toLocal(event.dateStart), toLocal(event.dateEnd)],
      externalCompanyIds: companyIds,
      contactIds: event.contacts?.map(c => c.id) ?? [],
    });
    setEventModalVisible(true);
  };

  const selectedEventCompanyIds: number[] = Form.useWatch('externalCompanyIds', eventForm) || [];

  const filteredEventContacts = useMemo(() => {
    const sourceList = relatedContacts.length > 0 ? relatedContacts : crmStore.contacts;
    if (!selectedEventCompanyIds || selectedEventCompanyIds.length === 0) {
      return sourceList;
    }
    return sourceList.filter(c => {
      const compId = c.externalCompanyId ?? c.externalCompany?.id;
      return compId != null && selectedEventCompanyIds.includes(compId);
    });
  }, [relatedContacts, crmStore.contacts, selectedEventCompanyIds]);

  const handleEventValuesChange = (changedValues: any, all: any) => {
    if (changedValues.externalCompanyIds) {
      const companyIds: number[] = changedValues.externalCompanyIds || [];
      if (companyIds.length > 0) {
        const currentContactIds: number[] = all.contactIds || [];
        const validContactIds = currentContactIds.filter(id => {
          const c = crmStore.contacts.find(ct => ct.id === id);
          const compId = c?.externalCompanyId ?? c?.externalCompany?.id;
          return compId != null && companyIds.includes(compId);
        });
        if (validContactIds.length !== currentContactIds.length) {
          eventForm.setFieldsValue({ contactIds: validContactIds });
        }
      }
    }
    const composite = buildCompositeName(all.externalCompanyIds, all.contactIds, all.dateRange);
    if (composite && composite !== all.name) {
      eventForm.setFieldsValue({ name: composite });
    }
  };

  const saveEvent = async () => {
    try {
      const { dateRange, externalCompanyIds, contactIds, ...rest } =
        await eventForm.validateFields();
      const payload = {
        ...rest,
        dateStart: toApi(dateRange[0]),
        dateEnd: toApi(dateRange[1]),
        externalCompanyIds: externalCompanyIds || [],
        contactIds: contactIds || [],
        name: buildCompositeName(externalCompanyIds, contactIds, dateRange),
      };
      if (editingEvent) {
        await crmStore.updateCrmEvent(editingEvent.id, payload);
      } else {
        await crmStore.addCrmEvent(payload);
      }
      setEventModalVisible(false);
      refreshEvents();
      message.success(editingEvent ? t('SavedSuccessfully') : t('CreatedSuccessfully'));
    } catch (err) {
      console.log('Validation error:', err);
    }
  };

  const deleteEvent = async (id: number) => {
    await crmStore.deleteCrmEvent(id);
    refreshEvents();
    message.success(t('DeletedSuccessfully'));
  };

  const contactColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '20%' },
    { title: t('Surname'), dataIndex: 'surname', key: 'surname', width: '20%' },
    { title: t('Email'), dataIndex: 'email', key: 'email', width: '20%' },
    { title: t('Phone'), dataIndex: 'phoneNumber', key: 'phoneNumber', width: '15%' },
    {
      title: t('Role'),
      dataIndex: 'roleId',
      key: 'roleId',
      width: '10%',
      render: (id: number) => crmStore.clientRoles.find(r => r.id === id)?.name,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: Contact) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditContact(record)} />
          <Popconfirm
            title={t('DeleteContactConfirm')}
            onConfirm={() => deleteContact(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const eventColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '25%' },
    {
      title: t('Start'),
      dataIndex: 'dateStart',
      key: 'dateStart',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('End'),
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('EventType'),
      dataIndex: 'typeId',
      key: 'typeId',
      width: '15%',
      render: (tid: number) => crmStore.CrmEventTypes.find(t => t.id === tid)?.name,
    },
    {
      title: t('Contacts'),
      key: 'contacts',
      width: '15%',
      render: (_: unknown, r: any) => {
        const cts = r.contacts || [];
        if (cts.length === 0) return '';
        if (cts.length === 1) return `${cts[0].name} ${cts[0].surname}`;
        return `${cts[0].name} ${cts[0].surname} +${cts.length - 1}`;
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditEvent(record)} />
          <Popconfirm
            title={t('DeleteEventConfirm')}
            onConfirm={() => deleteEvent(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: t('Home'), path: `/${applicationStore.company?.Tenant}` },
    { label: 'CRM', path: `/${applicationStore.company?.Tenant}/crm` },
    { label: t('Companies'), path: `/${applicationStore.company?.Tenant}/crm/companies` },
    {
      label: isCreateMode ? t('NewCompany') : t('CompanyDetails'),
      path: isCreateMode
        ? `/${applicationStore.company?.Tenant}/crm/company/new`
        : `/${applicationStore.company?.Tenant}/crm/company/${companyIdNumber}`,
    },
  ];

  return (
    <ERPLayout breadcrumbTitle={t('CompanyDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading}>
        <Card bordered={false} className="crm-event-form-card">
          <div className="crm-detail-top-nav" style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/companies`)}
              className="crm-back-button"
            >
              {t('Back')}
            </Button>
          </div>
          <CrmAuditInfo record={companyRecord} />
          <Tabs activeKey={activeTab} onChange={setActiveTab} className="crm-modern-tabs">
            <TabPane tab={t('Details')} key="details">
              <div className="crm-event-editor-container">
                {/* Header title & subtitle */}
                <div className="crm-event-form-header">
                  <div className="crm-event-form-title">
                    {isCreateMode
                      ? t('crm.companyParameters', 'Параметри компанії')
                      : companyRecord?.name || crmStore.externalCompanies.find(c => c.id === companyIdNumber)?.name || t('CompanyDetails')}
                  </div>
                  <div className="crm-event-form-subtitle">
                    {isCreateMode
                      ? t('crm.companySubtitle', 'Заповніть реквізити та дані організації CRM')
                      : t('crm.companyEditSubtitle', 'Перегляд та редагування даних компанії CRM')}
                  </div>
                </div>

                <Form form={form} layout="vertical" disabled={!editing} className="crm-event-modern-form">
                  {/* Group 1: General Information / Requisites */}
                  <div className="crm-form-section-title">
                    {t('crm.companyReqsLabel', 'ОСНОВНІ РЕКВІЗИТИ')}
                  </div>

                  {/* Company Name */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('Name')} <span className="crm-required">*</span>
                      </label>
                    </div>
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: t('PleaseEnterName') }]}
                      style={{ marginBottom: 4 }}
                    >
                      <Input placeholder={t('PleaseEnterName')} className="crm-modern-input" />
                    </Form.Item>
                  </div>

                  {editing && companyNameDuplicates?.exactMatch && (
                    <DuplicateWarning
                      type="error"
                      message={t('CompanyWithNameExists')}
                      items={[
                        {
                          id: companyNameDuplicates.exactMatch.id,
                          title: companyNameDuplicates.exactMatch.name,
                          details: [
                            companyNameDuplicates.exactMatch.edrpou
                              ? { label: t('EDRPOU'), value: companyNameDuplicates.exactMatch.edrpou }
                              : null,
                            companyNameDuplicates.exactMatch.addresses?.[0]
                              ? {
                                  label: t('Address'),
                                  value: [
                                    companyNameDuplicates.exactMatch.addresses[0].locationName,
                                    companyNameDuplicates.exactMatch.addresses[0].city,
                                    companyNameDuplicates.exactMatch.addresses[0].country,
                                  ]
                                    .filter(Boolean)
                                    .join(', '),
                                }
                              : null,
                          ].filter(Boolean) as any[],
                          linkPath: `${tenantPath}/crm/company/${companyNameDuplicates.exactMatch.id}`,
                        },
                      ]}
                    />
                  )}

                  {editing &&
                    !companyNameDuplicates?.exactMatch &&
                    (companyNameDuplicates?.similarMatches.length ?? 0) > 0 && (
                      <DuplicateWarning
                        type="warning"
                        message={t('SimilarCompaniesFound')}
                        items={companyNameDuplicates!.similarMatches.map((c) => ({
                          id: c.id,
                          title: c.name,
                          details: [
                            c.edrpou ? { label: t('EDRPOU'), value: c.edrpou } : null,
                            c.addresses?.[0]
                              ? {
                                  label: t('Address'),
                                  value: [
                                    c.addresses[0].locationName,
                                    c.addresses[0].city,
                                    c.addresses[0].country,
                                  ]
                                    .filter(Boolean)
                                    .join(', '),
                                }
                              : null,
                          ].filter(Boolean) as any[],
                          linkPath: `${tenantPath}/crm/company/${c.id}`,
                        }))}
                      />
                    )}

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">{t('EDRPOU')}</label>
                        </div>
                        <Form.Item name="edrpou" style={{ marginBottom: 4 }}>
                          <Input placeholder={t('EDRPOU')} className="crm-modern-input" />
                        </Form.Item>
                      </div>
                      {editing && companyEdrpouDuplicate && (
                        <DuplicateWarning
                          type="error"
                          message={t('CompanyWithEdrpouExists')}
                          items={[
                            {
                              id: companyEdrpouDuplicate.id,
                              title: companyEdrpouDuplicate.name,
                              details: [
                                { label: t('EDRPOU'), value: companyEdrpouDuplicate.edrpou || '' },
                                companyEdrpouDuplicate.addresses?.[0]
                                  ? {
                                      label: t('Address'),
                                      value: [
                                        companyEdrpouDuplicate.addresses[0].locationName,
                                        companyEdrpouDuplicate.addresses[0].city,
                                        companyEdrpouDuplicate.addresses[0].country,
                                      ]
                                        .filter(Boolean)
                                        .join(', '),
                                    }
                                  : null,
                              ].filter(Boolean) as any[],
                              linkPath: `${tenantPath}/crm/company/${companyEdrpouDuplicate.id}`,
                            },
                          ]}
                        />
                      )}
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">{t('Category')}</label>
                        </div>
                        <Form.Item name="categoryId" style={{ marginBottom: 0 }}>
                          <Select
                            allowClear
                            placeholder={t('SelectCategory')}
                            className="crm-modern-select"
                            showSearch
                            optionFilterProp="children"
                          >
                            {crmStore.categories.map((cat) => (
                              <Option key={cat.id} value={cat.id}>
                                {cat.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <div className="crm-form-section-divider" />

                  {/* Group 2: Description */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">{t('Description')}</label>
                    </div>
                    <Form.Item name="description" style={{ marginBottom: 0 }}>
                      <TextArea rows={4} placeholder={t('Description')} className="crm-modern-textarea" />
                    </Form.Item>
                  </div>

                  <div className="crm-form-section-divider" />

                  {/* Group 3: Addresses */}
                  <div className="crm-form-section-title">
                    {t('crm.companyAddressesLabel', 'АДРЕСИ ТА ЛОКАЦІЇ')}
                  </div>
                  <Form.List name="addresses">
                    {(fields, { add, remove }) => (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {fields.map(({ key, name, ...rest }) => (
                          <div
                            key={key}
                            style={{
                              padding: '16px',
                              background: '#f8fafc',
                              borderRadius: 10,
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            <Row gutter={12} align="top">
                              <Col span={editing ? 22 : 24}>
                                {editing && (
                                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                    <Input
                                      className="crm-modern-input"
                                      placeholder={t('PasteAddress', 'Paste an address from Google Maps or text and press Parse')}
                                      value={addressPaste[name] || ''}
                                      onChange={(e) => setAddressPaste((prev) => ({ ...prev, [name]: e.target.value }))}
                                      onPressEnter={() => parseAddressInto(name)}
                                      disabled={!!addressParsing[name]}
                                      allowClear
                                    />
                                    <Button
                                      type="primary"
                                      ghost
                                      icon={<ThunderboltOutlined />}
                                      loading={!!addressParsing[name]}
                                      disabled={!(addressPaste[name] || '').trim()}
                                      onClick={() => parseAddressInto(name)}
                                      style={{
                                        borderRadius: 8,
                                        height: 40,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        borderColor: '#3b82f6',
                                        color: '#2563eb',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {t('ParseAddress', 'Parse')}
                                    </Button>
                                  </div>
                                )}
                                <Row gutter={[10, 10]}>
                                  <Col xs={24} sm={12}>
                                    <Form.Item {...rest} name={[name, 'country']} style={{ marginBottom: 0 }}>
                                      <Input placeholder={t('Country')} className="crm-modern-input" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} sm={12}>
                                    <Form.Item {...rest} name={[name, 'state']} style={{ marginBottom: 0 }}>
                                      <Input placeholder={t('StateRegion')} className="crm-modern-input" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} sm={16}>
                                    <Form.Item {...rest} name={[name, 'city']} style={{ marginBottom: 0 }}>
                                      <Input placeholder={t('CityDistrict')} className="crm-modern-input" />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} sm={8}>
                                    <Form.Item {...rest} name={[name, 'zipCode']} style={{ marginBottom: 0 }}>
                                      <Input placeholder={t('ZipCode')} className="crm-modern-input" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={24}>
                                    <Form.Item {...rest} name={[name, 'addressLine1']} style={{ marginBottom: 0 }}>
                                      <Input placeholder={t('AddressLine')} className="crm-modern-input" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={24}>
                                    <Form.Item {...rest} name={[name, 'locationName']} style={{ marginBottom: 0 }}>
                                      <Input placeholder={t('LocationName')} className="crm-modern-input" />
                                    </Form.Item>
                                  </Col>
                                  <Form.Item {...rest} name={[name, 'latitude']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item {...rest} name={[name, 'longitude']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item {...rest} name={[name, 'plusCode']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Col span={24}>
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, cur) =>
                                        prev?.addresses?.[name] !== cur?.addresses?.[name]
                                      }
                                    >
                                      {() => {
                                        const a = form.getFieldValue(['addresses', name]);
                                        const url = mapUrl(a);
                                        const hasCoords = a?.latitude != null && a?.longitude != null;
                                        if (!url) return null;
                                        return (
                                          <div className="crm-address-coords" style={{ marginTop: 4 }}>
                                            <EnvironmentOutlined style={{ color: '#2563eb' }} />
                                            <span className="crm-address-coords-text">
                                              {hasCoords
                                                ? `${Number(a.latitude).toFixed(5)}, ${Number(a.longitude).toFixed(5)}${
                                                    a.plusCode ? ` · ${a.plusCode}` : ''
                                                  }`
                                                : t('CoordinatesUnknown')}
                                            </span>
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                              {t('OpenOnMap')}
                                            </a>
                                          </div>
                                        );
                                      }}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Col>
                              {editing && (
                                <Col span={2} style={{ textAlign: 'center', paddingTop: 8 }}>
                                  <Button
                                    type="text"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() => remove(name)}
                                  />
                                </Col>
                              )}
                            </Row>
                          </div>
                        ))}
                        {editing && (
                          <Button
                            type="dashed"
                            block
                            icon={<PlusCircleOutlined />}
                            onClick={() => add()}
                            style={{ height: 40, borderRadius: 8 }}
                          >
                            {t('AddAddress')}
                          </Button>
                        )}
                      </div>
                    )}
                  </Form.List>

                  {/* Form Actions in footer */}
                  <div className="crm-event-form-actions">
                    <div className="crm-event-actions-left">
                      {!isCreateMode && (
                        <Popconfirm title={t('DeleteCompanyConfirm')} onConfirm={onDelete}>
                          <Button danger icon={<DeleteOutlined />} className="crm-form-delete-btn">
                            {t('Delete')}
                          </Button>
                        </Popconfirm>
                      )}
                    </div>

                    <div className="crm-event-actions-right">
                      {editing && !isCreateMode ? (
                        <Button className="crm-form-cancel-btn" onClick={handleCancel}>
                          {t('Cancel')}
                        </Button>
                      ) : (
                        <Button
                          className="crm-form-cancel-btn"
                          onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/companies`)}
                        >
                          {t('Cancel')}
                        </Button>
                      )}

                      {editing ? (
                        <Button
                          type="primary"
                          className="crm-form-submit-btn"
                          icon={<CheckOutlined />}
                          onClick={onSave}
                        >
                          {isCreateMode ? t('crm.createCompanyBtn', 'Створити компанію') : t('crm.saveChangesBtn', 'Зберегти зміни')}
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          className="crm-form-submit-btn"
                          icon={<EditOutlined />}
                          onClick={() => setEditing(true)}
                        >
                          {t('Edit')}
                        </Button>
                      )}
                    </div>
                  </div>
                </Form>
              </div>
            </TabPane>

            {!isCreateMode && (
              <>
                <TabPane tab={t('Contacts')} key="contacts">
                  <div style={{ paddingTop: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                      <Button type="primary" icon={<PlusOutlined />} onClick={openAddContact}>
                        {t('AddContact')}
                      </Button>
                    </div>
                    <Table
                      dataSource={relatedContacts}
                      columns={contactColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      onRow={rec => ({
                        onDoubleClick: () =>
                          navigate(`/${applicationStore.company?.Tenant}/crm/contact/${rec.id}`),
                      })}
                    />
                  </div>
                </TabPane>

                <TabPane tab={t('Events')} key="events">
                  <div style={{ paddingTop: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                      <Button type="primary" icon={<PlusOutlined />} onClick={openAddEvent}>
                        {t('AddEvent')}
                      </Button>
                    </div>
                    <Table
                      dataSource={relatedEvents}
                      columns={eventColumns}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      expandable={{
                        expandedRowRender: rec => (
                          <div style={{ padding: 16 }}>
                            <Text strong>{t('Description')}:</Text>
                            <Typography.Paragraph>
                              {rec.description || t('NoDescription')}
                            </Typography.Paragraph>
                          </div>
                        ),
                      }}
                      onRow={rec => ({
                        onDoubleClick: () =>
                          navigate(`/${applicationStore.company?.Tenant}/crm/event/${rec.id}`),
                      })}
                    />
                  </div>
                </TabPane>

                <TabPane tab={t('Files')} key="files">
                  {companyIdNumber !== null && (
                    <CrmAttachmentsTab entityType="ExternalCompany" entityId={companyIdNumber} active={activeTab === 'files'} />
                  )}
                </TabPane>

                <TabPane tab={t('History')} key="history">
                  {companyIdNumber !== null && (
                    <CrmHistoryTab entityType="ExternalCompany" entityId={companyIdNumber} active={activeTab === 'history'} />
                  )}
                </TabPane>
              </>
            )}
          </Tabs>
        </Card>

        <Modal
          title={
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', paddingBottom: 4 }}>
              {editingContact ? t('EditContact') : t('AddContact')}
            </div>
          }
          open={contactModalOpen}
          onCancel={() => setContactModalOpen(false)}
          width={840}
          destroyOnClose
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setContactModalOpen(false)}
              className="crm-form-cancel-btn"
              style={{ minWidth: 100, height: 38 }}
            >
              {t('Cancel')}
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={saveContact}
              disabled={hasExactContactDuplicate}
              className="crm-form-submit-btn"
              style={{ minWidth: 140, height: 38, background: '#3b82f6' }}
            >
              {editingContact ? t('crm.saveChanges', 'Зберегти зміни') : t('crm.createContact', 'Створити контакт')}
            </Button>,
          ]}
        >
          <Form form={contactForm} layout="vertical">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                {/* Section: Basic Data */}
                <div className="crm-form-section-title" style={{ marginTop: 4 }}>
                  {t('crm.generalInfoLabel', 'ОСНОВНІ ДАНІ')}
                </div>

                <Row gutter={12}>
                  <Col span={12}>
                    <div className="crm-form-group">
                      <div className="crm-form-label-row">
                        <label className="crm-form-label">
                          {t('Name')} <span className="crm-required">*</span>
                        </label>
                      </div>
                      <Form.Item
                        name="name"
                        rules={[{ required: true, message: t('PleaseEnterName') }]}
                        style={{ marginBottom: 4 }}
                      >
                        <Input className="crm-modern-input" placeholder={t('Name')} />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="crm-form-group">
                      <div className="crm-form-label-row">
                        <label className="crm-form-label">
                          {t('Surname')} <span className="crm-required">*</span>
                        </label>
                      </div>
                      <Form.Item
                        name="surname"
                        rules={[{ required: true, message: t('PleaseEnterSurname') }]}
                        style={{ marginBottom: 4 }}
                      >
                        <Input className="crm-modern-input" placeholder={t('Surname')} />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                {contactNameDuplicates?.exactMatch && (
                  <DuplicateWarning
                    type="error"
                    message={t('ContactWithNameExists')}
                    items={[
                      {
                        id: contactNameDuplicates.exactMatch.id,
                        title: `${contactNameDuplicates.exactMatch.name} ${contactNameDuplicates.exactMatch.surname}`,
                        details: [
                          contactNameDuplicates.exactMatch.externalCompany?.name
                            ? { label: t('Company'), value: contactNameDuplicates.exactMatch.externalCompany.name }
                            : null,
                          contactNameDuplicates.exactMatch.email
                            ? { label: t('Email'), value: contactNameDuplicates.exactMatch.email }
                            : null,
                          contactNameDuplicates.exactMatch.phoneNumber
                            ? { label: t('Phone'), value: contactNameDuplicates.exactMatch.phoneNumber }
                            : null,
                        ].filter(Boolean) as any[],
                        linkPath: `${tenantPath}/crm/contact/${contactNameDuplicates.exactMatch.id}`,
                      },
                    ]}
                  />
                )}
                {!contactNameDuplicates?.exactMatch &&
                  (contactNameDuplicates?.similarMatches.length ?? 0) > 0 && (
                    <DuplicateWarning
                      type="warning"
                      message={t('SimilarContactsFound')}
                      items={contactNameDuplicates!.similarMatches.map((c) => ({
                        id: c.id,
                        title: `${c.name} ${c.surname}`,
                        details: [
                          c.externalCompany?.name
                            ? { label: t('Company'), value: c.externalCompany.name }
                            : null,
                          c.email ? { label: t('Email'), value: c.email } : null,
                          c.phoneNumber ? { label: t('Phone'), value: c.phoneNumber } : null,
                        ].filter(Boolean) as any[],
                        linkPath: `${tenantPath}/crm/contact/${c.id}`,
                      }))}
                    />
                  )}

                <div className="crm-form-group" style={{ marginTop: 8 }}>
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Role')}</label>
                  </div>
                  <Form.Item name="roleId" style={{ marginBottom: 8 }}>
                    <Select
                      allowClear
                      placeholder={t('Select')}
                      className="crm-modern-select"
                      showSearch
                      optionFilterProp="children"
                    >
                      {crmStore.clientRoles.map((r) => (
                        <Option key={r.id} value={r.id}>
                          {r.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="crm-form-section-divider" style={{ margin: '14px 0' }} />

                {/* Section: Contact Info */}
                <div className="crm-form-section-title">
                  {t('crm.contactInfoLabel', 'ЗВ\'ЯЗОК ТА КОНТАКТНІ ДАНІ')}
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Email')}</label>
                  </div>
                  <Form.Item
                    name="email"
                    style={{ marginBottom: 4 }}
                    rules={[{ type: 'email', message: t('InvalidEmailFormat') }]}
                  >
                    <Input type="email" className="crm-modern-input" placeholder="example@company.com" />
                  </Form.Item>
                </div>
                {contactEmailDuplicate && (
                  <DuplicateWarning
                    type="error"
                    message={t('ContactWithEmailExists')}
                    items={[
                      {
                        id: contactEmailDuplicate.id,
                        title: `${contactEmailDuplicate.name} ${contactEmailDuplicate.surname}`,
                        details: [
                          contactEmailDuplicate.externalCompany?.name
                            ? { label: t('Company'), value: contactEmailDuplicate.externalCompany.name }
                            : null,
                          contactEmailDuplicate.email
                            ? { label: t('Email'), value: contactEmailDuplicate.email }
                            : null,
                        ].filter(Boolean) as any[],
                        linkPath: `${tenantPath}/crm/contact/${contactEmailDuplicate.id}`,
                      },
                    ]}
                  />
                )}

                <div className="crm-form-group" style={{ marginTop: 8 }}>
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Phone')}</label>
                  </div>
                  <Form.Item
                    name="phoneNumber"
                    style={{ marginBottom: 4 }}
                    rules={[
                      {
                        validator(_, value) {
                          if (!value) return Promise.resolve();
                          if (!isValidPhoneNumber(value)) {
                            return Promise.reject(new Error(t('InvalidPhoneFormat')));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input className="crm-modern-input" placeholder="+380..." />
                  </Form.Item>
                </div>
                {contactPhoneDuplicate && (
                  <DuplicateWarning
                    type="error"
                    message={t('ContactWithPhoneExists')}
                    items={[
                      {
                        id: contactPhoneDuplicate.id,
                        title: `${contactPhoneDuplicate.name} ${contactPhoneDuplicate.surname}`,
                        details: [
                          contactPhoneDuplicate.externalCompany?.name
                            ? { label: t('Company'), value: contactPhoneDuplicate.externalCompany.name }
                            : null,
                          contactPhoneDuplicate.phoneNumber
                            ? { label: t('Phone'), value: contactPhoneDuplicate.phoneNumber }
                            : null,
                        ].filter(Boolean) as any[],
                        linkPath: `${tenantPath}/crm/contact/${contactPhoneDuplicate.id}`,
                      },
                    ]}
                  />
                )}

                <Form.Item name="externalCompanyId" hidden>
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <div className="crm-form-section-title" style={{ marginTop: 4 }}>
                  {t('crm.additionalInfoLabel', 'ДОДАТКОВІ ДАНІ')}
                </div>
                <Form.List name="additionalInfos">
                  {(fields, { add, remove }) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {fields.map(({ key, name, ...restField }) => {
                        const currentInfo = watchedContactAdditionalInfos?.[name];
                        const duplicateInfo =
                          currentInfo?.type && currentInfo?.name
                            ? checkAdditionalInfoDuplicate(
                                name,
                                currentInfo.type,
                                currentInfo.name,
                                watchedContactAdditionalInfos,
                                crmStore.contacts,
                                crmStore.contactTypes,
                                editingContact?.id,
                                watchedContactPhone,
                                watchedContactEmail,
                              )
                            : undefined;

                        return (
                          <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div
                              style={{
                                display: 'flex',
                                gap: 10,
                                alignItems: 'center',
                                width: '100%',
                              }}
                            >
                              <Form.Item
                                {...restField}
                                name={[name, 'type']}
                                className="crm-no-explain"
                                style={{ marginBottom: 0, width: 140, flexShrink: 0 }}
                                rules={[{ required: true, message: t('PleaseSelectType') }]}
                              >
                                <Select
                                  placeholder={t('Type')}
                                  allowClear
                                  showSearch
                                  className="crm-modern-select"
                                >
                                  {contactTypeOptions.map((typeName) => (
                                    <Option key={typeName} value={typeName}>
                                      {typeName}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, 'name']}
                                className="crm-no-explain"
                                style={{ marginBottom: 0, flex: 1 }}
                                rules={[
                                  { required: true, message: t('PleaseEnterValue') },
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      if (!value) return Promise.resolve();
                                      const currentType = getFieldValue(['additionalInfos', name, 'type']);
                                      const typeCfg = crmStore.contactTypes.find(
                                        (ct) => ct.name?.toLowerCase().trim() === (currentType || '').toLowerCase().trim()
                                      );
                                      const kind = typeCfg?.validationKind;

                                      if (kind === ContactValidationKind.PHONE) {
                                        if (!isValidPhoneNumber(value)) {
                                          return Promise.reject(new Error(t('InvalidPhoneFormat')));
                                        }
                                      } else if (kind === ContactValidationKind.EMAIL) {
                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                        if (!emailRegex.test(value.trim())) {
                                          return Promise.reject(new Error(t('InvalidEmailFormat')));
                                        }
                                      } else if (kind === ContactValidationKind.URL) {
                                        const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
                                        if (!urlRegex.test(value.trim())) {
                                          return Promise.reject(new Error(t('InvalidUrlFormat')));
                                        }
                                      }

                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <Input className="crm-modern-input" placeholder={t('Value')} />
                              </Form.Item>
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                style={{
                                  height: 36,
                                  width: 36,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 8,
                                }}
                              />
                            </div>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, cur) =>
                                prev?.additionalInfos?.[name] !== cur?.additionalInfos?.[name]
                              }
                            >
                              {() => {
                                const typeErr = contactForm.getFieldError(['additionalInfos', name, 'type']);
                                const valErr = contactForm.getFieldError(['additionalInfos', name, 'name']);
                                const err = typeErr?.[0] || valErr?.[0];
                                if (!err) return null;
                                return (
                                  <div className="crm-field-error-row">
                                    {err}
                                  </div>
                                );
                              }}
                            </Form.Item>
                            {duplicateInfo && (
                              <DuplicateWarning
                                type="warning"
                                message={
                                  duplicateInfo.isInternalDuplicate
                                    ? t('DuplicateInCurrentContact')
                                    : t('AdditionalInfoAlreadyUsed')
                                }
                                items={
                                  duplicateInfo.externalContact
                                    ? [
                                        {
                                          id: duplicateInfo.externalContact.id,
                                          title: `${duplicateInfo.externalContact.name} ${duplicateInfo.externalContact.surname}`,
                                          details: [
                                            duplicateInfo.externalContact.externalCompany?.name
                                              ? { label: t('Company'), value: duplicateInfo.externalContact.externalCompany.name }
                                              : null,
                                            { label: currentInfo?.type || '', value: duplicateInfo.matchedValue || '' },
                                          ].filter(Boolean) as any[],
                                          linkPath: `${tenantPath}/crm/contact/${duplicateInfo.externalContact.id}`,
                                        },
                                      ]
                                    : []
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusCircleOutlined />}
                        className="crm-add-field-btn"
                        style={{
                          height: 38,
                          borderRadius: 8,
                          borderStyle: 'dashed',
                          borderColor: '#cbd5e1',
                          color: '#475569',
                          fontWeight: 500,
                          marginTop: 4,
                        }}
                      >
                        {t('AddField')}
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Col>
            </Row>
          </Form>
        </Modal>

        <Modal
          title={
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', paddingBottom: 4 }}>
              {editingEvent ? t('EditEvent') : t('AddEvent')}
            </div>
          }
          open={eventModalVisible}
          onCancel={() => setEventModalVisible(false)}
          width={840}
          destroyOnClose
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setEventModalVisible(false)}
              className="crm-form-cancel-btn"
              style={{ minWidth: 100, height: 38 }}
            >
              {t('Cancel')}
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={saveEvent}
              className="crm-form-submit-btn"
              style={{ minWidth: 140, height: 38, background: '#3b82f6' }}
            >
              {editingEvent ? t('crm.saveChanges', 'Зберегти зміни') : t('crm.createEvent', 'Створити подію')}
            </Button>,
          ]}
        >
          <Form form={eventForm} layout="vertical" onValuesChange={handleEventValuesChange}>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <div className="crm-form-section-title" style={{ marginTop: 4 }}>
                  {t('crm.generalInfoLabel', 'ОСНОВНІ ДАНІ')}
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('Companies')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="externalCompanyIds"
                    rules={[
                      {
                        validator(_, value) {
                          if (!value || value.length === 0) {
                            return Promise.reject(new Error(t('PleaseSelectCompany')));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 8 }}
                  >
                    <FormCheckboxDropdown
                      options={crmStore.externalCompanies}
                      placeholder={t('crm.chooseCompanyPlaceholder', '+ Оберіть або введіть компанію...')}
                      searchPlaceholder={t('Search')}
                    />
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Contacts')}</label>
                  </div>
                  <Form.Item name="contactIds" style={{ marginBottom: 8 }}>
                    <FormCheckboxDropdown
                      options={filteredEventContacts.map(c => ({ id: c.id, name: `${c.name} ${c.surname}`.trim() }))}
                      placeholder={t('crm.chooseContactPlaceholder', '+ Додати контакт...')}
                      searchPlaceholder={t('Search')}
                    />
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('EventType')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="typeId"
                    rules={[{ required: true, message: t('PleaseSelectEventType') }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Select placeholder={t('Select')} allowClear className="crm-modern-select">
                      {crmStore.CrmEventTypes.map(type => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('DateTime')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="dateRange"
                    rules={[{ required: true, message: t('PleaseSelectDateTime') }]}
                    style={{ marginBottom: 4 }}
                  >
                    <RangePicker
                      showTime
                      allowClear={false}
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: '100%', height: 38, borderRadius: 8 }}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div className="crm-form-section-title" style={{ marginTop: 4 }}>
                  {t('crm.eventDetailsLabel', 'ДЕТАЛІ ПОДІЇ')}
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('Name')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item name="name" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                    <Input disabled className="crm-modern-input" style={{ background: '#f8fafc' }} />
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Description')}</label>
                  </div>
                  <Form.Item name="description" style={{ marginBottom: 4 }}>
                    <TextArea
                      rows={6}
                      placeholder={t('Description')}
                      style={{ borderRadius: 8, borderColor: '#e2e8f0', resize: 'vertical' }}
                    />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal>
      </Spin>
    </ERPLayout>
  );
});

export default ExternalCompanyDetail;
